import type { Base } from 'active-resource';
import type { GConstructor } from './APILibrary';
import APILibrary, { ClassExtender, GenerateResource } from './APILibrary';
import { EducationLevelResource } from './EducationLevel';
import format from 'date-fns/format';
import { normalizeAbsoluteDate } from 'util/formatting/dates';

/**
 * Relationship classes
 */
export class EducationHistoryAttributes {
  city: string = null;
  completedCollegeCredits: number = null;
  current: boolean = null;
  educationLevelId: string = null;
  endDate: string = null;
  filterableEducationLevel: string = null;
  id: string = null;
  major: EducationLevelResource = new EducationLevelResource();
  majorId: number = null;
  minor: EducationLevelResource = new EducationLevelResource();
  minorId: number = null;
  school: string = null;
  startDate: string = null;
  transcript: string = null;
}

export class EducationHistoryBelongsTo {
  educationLevel: () => EducationLevelResource = () => new EducationLevelResource();
}

export class EducationHistoryHasOne {}
export class EducationHistoryHasMany {}

/**
 * Utility interfaces
 */
export interface EducationHistoryMethods {
  getEducationTitle: () => string;
  getEducationProgressText: () => string;
  getEducationDurationText: () => string;
  areDatesValid: () => boolean;
  isResourceValid: () => boolean;
}

export interface EducationHistoryInterface
  extends EducationHistoryAttributes,
    EducationHistoryBelongsTo,
    EducationHistoryType {}

type EducationHistoryType = GConstructor<EducationHistoryInterface>;

export interface EducationHistoryResource
  extends Base<EducationHistoryAttributes>,
    EducationHistoryInterface,
    EducationHistoryMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<EducationHistoryAttributes>({
    queryName: 'user_profile/education_histories',
    className: 'EducationHistory',
    AttributesClass: EducationHistoryAttributes,
    BelongsToClasses: [
      {
        Klass: EducationHistoryBelongsTo,
      },
    ],
  });

export const EducationHistoryResourceClass = <TBase extends EducationHistoryType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements EducationHistoryMethods {
    getEducationTitle() {
      const educationLevelTitle = this.educationLevel()?.title;
      const majorTitle = this.major?.title;

      if (!educationLevelTitle && !majorTitle) {
        return '';
      }

      if (!educationLevelTitle || !majorTitle) {
        return educationLevelTitle || majorTitle;
      }

      if (['Bachelor', 'Master', 'Doctor'].includes(educationLevelTitle)) {
        return `${educationLevelTitle}'s in ${majorTitle}`;
      }

      return `${educationLevelTitle} in ${majorTitle}`;
    }

    getEducationProgressText() {
      return `${this.getEducationTitle()} ${this.current ? 'in Progress' : 'Completed'}`;
    }

    areDatesValid() {
      return this.startDate && ((this.endDate && this.startDate <= this.endDate) || this.current);
    }

    educationLevelRequiresMajor() {
      return this.filterableEducationLevel !== 'high_school';
    }

    isResourceValid() {
      return Boolean(
        this.areDatesValid() &&
          this.filterableEducationLevel &&
          this.school &&
          this.city &&
          ((this.educationLevelRequiresMajor() && (this.major?.id || this.majorId)) ||
            !this.educationLevelRequiresMajor()),
      );
    }

    getEducationDurationText() {
      if (!this.startDate || (!this.current && !this.endDate)) {
        return '';
      }

      const formattedStartDate = format(normalizeAbsoluteDate(new Date(this.startDate)), 'MMM yy');

      if (this.current) {
        return `${formattedStartDate} - Present`;
      }

      const formattedEndDate = format(normalizeAbsoluteDate(new Date(this.endDate)), 'MMM yy');

      if (formattedStartDate === formattedEndDate) {
        return formattedStartDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  };

export class EducationHistoryResource extends ClassExtender<EducationHistoryInterface>(
  BaseClass,
  EducationHistoryResourceClass,
) {}

/**
 * Resource instance
 */
export const EducationHistory =
  APILibrary.createResource<EducationHistoryResource>(EducationHistoryResource);
